// @flow weak

import update from 'immutability-helper'
import moment from 'moment'
import memoize from 'memoizerific'
import { basename } from 'path'

import {
  find, findLast, chain, isEqual, values, last, sum, flatten, countBy, flatMap,
  compact, findLastIndex, keyBy, size, sortBy, reverse, keys, isNumber, get, isEmpty
} from "lodash"

import { round, randstr, str2moment, l } from "../lib/Tools"
import { emptyPrice, normalizePrice } from "../lib/prices"
import { eur2yuan } from "../app/lib/exchangeRateClient"

import {
  transportationIncludesTake, getTransportationLabel, translateCity, getText, getTextWithTokens, getLocale
} from '../app/lib/text.js'

const TRIPS_CACHE = 500
const LOG_CACHE = false

/**
 * numbers are hard
 */

// Note: isNaN and lo's isNumber are very different:
//                        const data =    [    6,    0,    "",  null, undefined,   NaN, 1/0  ]
// data.map(x => !Number.isNaN(x))   =>   [ true, true,  true,  true,      true, false, true ]
// data.map(x => lo.isNumber(x))     =>   [ true, true, false, false,     false,  true, true ]

function zeroIfNaN(x) { return Number.isNaN(x) ? 0 : x }
function numCoerce(x, c) { return isNumber(x) ? x : c }

/**
 * Trip People management
 */

// #TODO these should be memoized may be yea?

export function getLeaderName(trip) {
  return (get(trip, "people.leader", null)
    ? get(trip, `people.data.${trip.people.leader}.name`)
    : null
  )
}

// can receive a trip
export function countAdults({ people }) {
  return values(people.data).filter(({ age }) => (age === null || age > 16)).length
}

// can receive a trip
export function countKids({ people }) {
  return values(people.data).filter(({ age }) => (isNumber(age) && age <= 16)).length
}

function groupByAges(people) {
  return countBy(
    values(people.data),
    person => numCoerce(person.age, 30)
  )
}

/**
 * Prices:
 */

/**
 * the price of an item depending on the number of ppl
 */
export function calculateItemPrice(price, people) {
  const np = normalizePrice(price)
  const pa = values(people.data)

  if (np.perGroup) return zeroIfNaN(np.general)

  else if (np.ageAware) {
    const countPerAge = groupByAges(people)

    const prices = keys(countPerAge).map(age => {
      const foundPricePoint = find(
        sortBy(np.agePrices, "age"),
        point => (point.age >= parseInt(age))
      )

      return (
        countPerAge[age] * (foundPricePoint ? (parseInt(foundPricePoint.price) || 0) : np.general)
      )
    })

    return zeroIfNaN(sum(prices))
  }

  else return zeroIfNaN((pa.length) * np.general)
}

export function calculateItemPriceForOne(price, age = 30) {
  return calculateItemPrice(price, {
    data: {
      pepito: { age, name: "pepito" }
    }
  })
}

function showCalculation(price, people) {
  const np = normalizePrice(price)
  const pa = values(people.data)

  if (price.perGroup) return `${price.general}€/${getText("grupo")}`

  else if (price.ageAware) {
    const countPerAge = groupByAges(people)

    const prices = keys(countPerAge).map(age => {
      const foundPricePoint = findLast(
        reverse(sortBy(price.agePrices, "age")),
        point => (point.age >= numCoerce(age, 30))
      )
      const value = (foundPricePoint ? parseInt(foundPricePoint.price) : price.general)
      return `${countPerAge[age]} ✕ ${value}`
    })

    return prices.join(" + ")
  }

  else return `${pa.length}✕${price.general}€`
}

export function getRouteLength(route) {
  return route.agenda.filter(x => x && x.type == "day").length
}

export function getTripLength(t) {
  if (!t || !t.endDate || !t.startDate) return null

  const a = str2moment(t.endDate)
  const b = str2moment(t.startDate)

  return (
    a.diff(b, "days") + 1 - (t.lastDayOnlyFlight ? 1 : 0)
  )
}

function findDayCity(dayIndex /* number */, agenda /* Array<Object> */) /* => string[] */ {
  const cityIds = new Set()

  for (let i = dayIndex + 1; i < agenda.length; i++) {
    if (agenda[i].type == "day") break
    if (agenda[i].city) cityIds.add(agenda[i].city)
  }

  return [...cityIds]
}

function dayObject2(accum, item, cities) {
  /* produces the "day" entry in the doc-pieces array */
  /* note: this is to be serialized, and array fields may get lost. thats why background1 and 2 */
  const t = accum.date.add(1, "day")
  const d = ++(accum.day)

  const bgs = compact(cities.map(c => (c && c.files && c.files.dayBg) || null))

  return {
    _id: `dd${d + 1}`,
    name: `${getText("dia_1")}${d}${getText("dia_2")}`,
    type: "doc1day",
    city: cities.map(c => c._id),
    chineseCity: cities.map(c => c.chinese),
    fixed: true,
    background: bgs,
    background0: bgs[0],
    background1: bgs[1] || null,
    description: item.description,
    day: d,
    date: t.date(),
    month: t.month(),
    year: t.year(),
  }
}

/**
 * get all pieces of this trip's doc1
 * gives a doc object for everything to be included in a trip document
 */

export function getTripFiles(trip, route, Activities, Cities) {
  // console.log(`-- getTripFiles(trip ${trip._id}, route ${route._id}, ${size(Activities)} activities, ${size(Cities)} cities)`)

  // 2017/02/01 random date, just to be able to generate prices without the date. may not be a great idea
  const mapAccumulator = { date: str2moment(trip.startDate || "2017/02/01").subtract(1, "day"), day: 0 }
  const includedCities = new Set()

  const agenda = route.agenda
  const expenses = route.expenses

  const content = compact(flatten(
    agenda.map((x, i) => {
      const res = []

      // first time in a city, add description
      if (x.type === "city" || x.type === "activity") {
        if (!includedCities.has(x.city)) {
          const city = Cities[x.city]

          if (city && city.files && city.files.descriptionPDF) {
            res.push({
              fixed: false,
              type: "description",
              _id: `desc-${x.city}`,
              name: `${getTextWithTokens("page_city", [translateCity(city)])}`,
              section: `presentation-${x.city}`,
              files: { doc1Page: city.files.descriptionPDF }
            })
          }

          includedCities.add(x.city)
        }
      }

      if (x.type === "day") {
        const dayCities = compact(findDayCity(i, agenda).map(id => Cities[id]))
        const obj = dayObject2(mapAccumulator, x, dayCities)
        res.push(obj)
      }
      else if (x.type === "activity") {
        res.push(update(Activities[x.id], { type: { $set: "activity" } }))
      }

      return res
    })
  ))

  // the last day is not a fixed object
  const lastDayIdx: number = findLastIndex(content, { type: "doc1day" })
  if (lastDayIdx >= 0) {
    const lastDayThings = content.length - lastDayIdx - 1
    const justOneThing = ((lastDayThings) == 1)
    const lastThing = last(content)
    const itsTripBack = lastThing && (lastThing.type == "city" && lastThing.city == "China")

    if ((lastDayThings == 0) || (justOneThing && itsTripBack)) {
      // so the last day doesnt have to be fixed! ^^
      content[lastDayIdx].fixed = false
    }
  }
  else {
    console.error(`didnt found the last day. go figure.`)
  }

  const lastCity: string = content[content.length - 1].city || ""
  const lastDay: number = content.reduce((ld, obj) => obj.day || ld, 1) + (trip.lastDayOnlyFlight ? 1 : 0)

  const returnPage = {
    _id: "return",
    type: "doc1return",
    name: getText("Vuelta"),
    fixed: true,
    lastCity,
    day: lastDay,
    date: mapAccumulator.date.date(),
    month: mapAccumulator.date.month(),
    text: trip.returnDescription
  }

  return [
    { _id: "cover", type: "doc1cover", name: getText("Portada"), fixed: true },
    { _id: "services", type: "doc1services", name: getText("Servicios"), fixed: true },
    ...content,
    returnPage,
    { _id: "ticket", type: "doc1ticket", name: getText("Presupuesto"), fixed: true },
    { _id: "bye", type: "doc1bye", name: getText("Contraportada"), fixed: true },
    { _id: "checklist", type: "doc1checklist", name: getText("Recuerda llevar..."), fixed: true },
  ]
}

export function getMissingTitleErrors(tripIgnored: Object, route: Object, Activities: Object): Array<string> {
  let errors = []

  // 1. Activities
  errors = errors.concat(
    route.agenda
      .filter(x => x.type === "activity")
      .map(x => Activities[x.id])
      .filter(a => !a.title)
      .map(a => `${getTextWithTokens("error_missing_title_1", [a.name])}`)
  )

  // 2. expenses
  errors = errors.concat(
    route.expenses
      .filter(e => !e.title)
      .map(e => `${getTextWithTokens("error_missing_title_2", [e.name])}`)
  )

  return errors
}

/**
 * generates all the pricing info for a trip, with the pricing of every item
 */

// non-memoized
export function __getTripBill(trip, route, activities, Cities, forceChinese, isTemplate) {
  if (!route || !route.agenda) return []

  if (LOG_CACHE) {
    console.log(`CACHE MISS -- _getTripBill(trip ${trip._id}, route ${route._id}, ${size(activities)} activities, ${size(Cities)} cities, forceChinese: ${forceChinese})`)
  }

  const language = forceChinese ? "ch" : null

  /* the bill is an array blocks, one per day, plus may be other blocks like "General Expenses"
   * each block also contains an array of items
   * we'll iterate through the agenda, creating the items and adding them to the bill
   */
  const bill = []
  bill.key = randstr()

  const agenda = compact(route.agenda)

  let lastBlockCities: ?Set<string> = null // set of all the cities i see in this block
  let dayCount = 1
  let lastDay = trip.startDate ? str2moment(trip.startDate) : moment()

  const itemsDict = keyBy(agenda, "key")
  const reps = {}
  const repDates = {}

  const adults = countAdults(trip)
  const kids = countKids(trip)

  /* create the bill */

  for (let i = 0; i < agenda.length; ++i) {
    const agendaItem = agenda[i]

    if (agendaItem.type === "day") {
      if (i > 0) {
        dayCount++;
      }

      lastDay = trip.startDate ? str2moment(trip.startDate).add(dayCount - 1, "days") : moment()
      const dayDate = trip.startDate ? str2moment(trip.startDate).add(dayCount - 1, "days").locale(getLocale()).format("YYYY/MM/DD dddd") : null

      if (bill.length > 0) {
        last(bill).cities = Array.from(lastBlockCities)
      }

      lastBlockCities = new Set([])

      bill.push({
        _id: `${trip._id}_d${dayCount}`,
        key: agendaItem.key,
        dayNumber: dayCount,
        date: lastDay.format("YYYY/MM/DD"),
        shortDate: lastDay.format("MM/DD"),
        section: `actividades día ${dayCount}`,
        sectionType: "day",
        sectionName:
          `${getText("desglose_section_1", language)} ${dayCount} ${getText("desglose_section_2", language)} (${dayDate || (getText("falta la fecha de inicio", language))})`,
        sectionTitle: `第${dayCount}天`,
        items: [],
        cities: [],
        agendaItem,
      })
    }

    else if (agendaItem.type === "city") {
      const t = agendaItem.transport
      const tto = t && t.to
      const origenIdx = findLastIndex(agenda, x => x.to || x.city, i - 1)
      const origen = origenIdx < 0 ? "China" : (agenda[origenIdx].from || agenda[origenIdx].city)

      const name1 = t ? getTransportationLabel(t.means, { forceLanguage: "es" }) : "NO TRANSPORT"
      // WTF is this
      const name2 = `${get(Cities, [origen, "name"], "???")} - ${tto ? get(Cities, [tto, "name"], "???") : "???"}`
      //const name2 = `${get(Cities, [origen, "name"], "???")} - ${t.to || "???"}`

      if (t) {
        last(bill).items.push({
          _id: `v-${origen}-${t.to}`,
          key: agendaItem.key,
          name: `${name1}: ${name2}`,
          // title: `Viaje de ${t.from} a ${t.to} en ${t ? t.means : "NO T"}`,
          title: (`从${Cities[origen] ? Cities[origen].chinese : origen}${transportationIncludesTake(t.means) ? `搭乘` : ""
            }${getTransportationLabel(t.means, { forceLanguage: "ch" })}`
            + `至${Cities[t.to] ? Cities[t.to].chinese : t.to}`),

          useCustomFee: agendaItem.useCustomFee,
          customFee: (100 + (agendaItem.customFee || 0)) / 100,

          /* eslint-disable space-in-parens */
          priceValue: calculateItemPrice(t.price, trip.people),
          costValue: calculateItemPrice(t.cost, trip.people),
          priceCalculation: showCalculation(t.price, trip.people),
          costCalculation: showCalculation(t.cost, trip.people),
          /* eslint-enable space-in-parens */
          price: t.price,
          agendaItem,
          type: agendaItem.type,
          time: agendaItem.time,
          day: dayCount,
          date: lastDay.format("YYYY/MM/DD"),

          means: t.means,
          origin: origen,
          destination: agendaItem.city,
          city: agendaItem.city,
          departureAddress: (
            // i want Beijing to == China
            (origen && origen != t.originCity && origen != (Cities[t.originCity] && Cities[t.originCity].country))
              ? null
              : (
                t.departureStationKey == null
                  ? t.departureStationCustom
                  : Cities[t.originCity].stations[t.departureStationKey].address
              )
          ),
          arrivalAddress: t.arrivalStationKey == null
            ? t.arrivalStationCustom
            : Cities[agendaItem.city].stations[t.arrivalStationKey].address,
          shouldGoToHotelFirst: t.shouldGoToHotelFirst,

          commuteFrom: t.commuteFrom,
          commuteTo: t.commuteTo,

          innerNote: t.notes || null
        })
      }
    }

    else if (agendaItem.type === "expense") {
      const expenseItem = (agendaItem.expense ? agendaItem : agenda.find(y => y.key == agendaItem.ref))
      const expense = expenseItem && expenseItem.expense
      const date = lastDay.format("YYYY/MM/DD") // #TODO optimize this away
      const k = agendaItem.ref || agendaItem.key

      // look for repetition and repetition dates (algo2)
      if (!reps[k]) {
        reps[k] = 1
        repDates[k] = [date]
      }
      else {
        reps[k] += 1
        if (!repDates[k]) console.error(`WTF, no ${k}`)
        repDates[k].push(date)
      }

      if (expense) {
        last(bill).items.push({
          _id: `e-${agendaItem.key}`,
          key: agendaItem.key,
          ref: agendaItem.ref || null,
          name: expense.name,
          title: expense.title,

          /* eslint-disable space-in-parens */
          priceValue: calculateItemPrice(expense.price, trip.people),
          costValue: calculateItemPrice(expense.cost, trip.people),
          priceCalculation: showCalculation(expense.price, trip.people),
          costCalculation: showCalculation(expense.cost, trip.people),
          /* eslint-enable space-in-parens */

          agendaItem,
          referencedAgendaItem: expenseItem,
          price: expense.price,
          type: expense.type == "hotel" ? "hotel" : "expense",
          time: agendaItem.time,
          day: dayCount,
          date,
          city: expense.city,
          useCustomFee: expense.useCustomFee,
          customFee: (100 + (expense.customFee || 0)) / 100,

          innerNote: expense.notes || null,

          address: expense.address,
          preciseLocation: expense.preciseLocation,
          image: (expense.files && expense.files.expenseImg) || null,
          recommendedTransportation: agendaItem.recommendedTransportation || null,
          recommendedTransportationOther: agendaItem.recommendedTransportationOther || null,
        })
      }
    }

    else if (agendaItem.type === "activity") {
      const a = activities[agendaItem.id]

      if (!a) {
        console.error(`actividad ${agendaItem.id} no se encontró`)
      }
      else {
        const price = (
          (agendaItem.useCustomPrice && (agendaItem.customPrice || emptyPrice))
          || (!isTemplate && agendaItem.frozenPrice)
          || a.price // activities in a template (no frozenPrice) or, god forbid, an activity wo/ frozenPrice
        )

        if (!isTemplate && !agendaItem.frozenPrice) {
          l(`this item has no frozen Price: ${agendaItem.key} in ${trip._id} (${trip.realName || trip.name})`)
        }

        lastBlockCities.add(a.city)
        last(bill).items.push({
          _id: a._id,
          key: agendaItem.key,
          name: a.name,
          title: a.title,
          useCustomPrice: agendaItem.useCustomPrice,
          useCustomFee: agendaItem.useCustomFee,
          customFee: (100 + (agendaItem.customFee || 0)) / 100,

          /* eslint-disable space-in-parens */
          priceValue: calculateItemPrice(price, trip.people),
          priceCalculation: showCalculation(price, trip.people),
          costValue: calculateItemPrice(a.cost, trip.people),
          costCalculation: showCalculation(a.cost, trip.people),
          /* eslint-enable space-in-parens */

          price,
          agendaItem,
          type: agendaItem.type,
          time: agendaItem.time,
          day: dayCount,
          date: lastDay.format("YYYY/MM/DD"),
          city: agendaItem.city,
          activityId: agendaItem.id,

          image: (a.files && a.files.activityImg && basename(a.files.activityImg)) || null,
          address: a.address,
          preciseLocation: a.preciseLocation,
        })
      }
    }

    // #TODO make sure this doesnt fuck something else up
    else if (agendaItem.type == "note") {
      last(bill).items.push({
        ...agendaItem,
        priceValue: 0,
        priceCalculation: "-",
        costValue: 0,
        costCalculation: "-",
      })
    }

    else {
      console.error(`Item of unknown type ${agendaItem.type}`)
      console.error(`Whole item: ${JSON.stringify(agendaItem)}`)
    }
  } // ~ main loop

  if (lastBlockCities && bill.length > 0) {
    last(bill).cities = Array.from(lastBlockCities)
  }

  // Repetition dates y número de repeticiones
  // fechas de las repeticiones de un gasto u hotel

  // ? const allItems = flatten(bill.map(section => section.items))

  // algo2
  bill.forEach((section, i) => {
    section.items.forEach((item, j) => {
      if (item.type == "expense" || item.type == "hotel") {
        const k = item.ref || item.key
        item.numRepetitions = reps[k]
        item.repetitionDates = repDates[k]
        item.totalPriceValue = item.numRepetitions * item.priceValue
        item.totalCostValue = item.numRepetitions * item.costValue
      }
    })
  })

  // Precios y costes de la ruta

  const generales = route.expenses.map(expense => ({
    ...expense,
    _id: expense._id,
    key: expense._id,
    name: expense.name,
    type: "other_expense",
    title: expense.title || expense.name,
    priceValue: calculateItemPrice(expense.price, trip.people),
    costValue: calculateItemPrice(expense.cost, trip.people),
    price: expense.price,
    date: trip.startDate,
    useCustomFee: expense.useCustomFee,
    customFee: (100 + (expense.customFee || 0)) / 100
  }))

  bill.push({
    _id: trip._id,
    section: "route",
    key: randstr(),
    sectionType: "expenses",
    sectionName: getText("Otros gastos", language),
    sectionTitle: "其它费用",
    items: generales
  })

  return bill
}

/** Note: on memoization
 * memoizerific doesnt allow functions with variable num of args,
 * so i wrap it in another function that ensures the optional argument
 *
 * also, the options object broke memoization. since it was an object-literal,
 * it was created anew every bloody time.
 */
export const _getTripBill = memoize(TRIPS_CACHE)(__getTripBill)

export const getTripBill = (trip, route, activities, Cities, opt = null) => (
  _getTripBill(trip, route, activities, Cities, (opt && opt.forceChinese) || false, (opt && opt.isTemplate) || false)
)

/**
 * price of trip
 */

export const _calculateTripPriceFromBill = (trip, bill) => {
  if (LOG_CACHE) console.log(`CACHE MISS -- _calculateTripPriceFromBill(trip ${trip._id}, bill ${bill.key})`)

  const dayPriceValues = bill.map(section => sum(section.items.map(item => item.priceValue)))
  const priceValues = flatMap(bill, section => section.items).map(item => item.priceValue)

  const calculatedBruteE = sum(priceValues)
  const calculatedBruteY = eur2yuan(calculatedBruteE)
  const factorGestion = (100 + trip.pGestion) / 100

  const calculatedFinalE = sum(bill.map(
    section => sum(
      section.items.map(
        item => (item.useCustomFee ? item.customFee : factorGestion) * item.priceValue
      )
    )
  ))

  const calculatedFinalY = eur2yuan(calculatedFinalE)

  const calculatedRoundedE = round(calculatedFinalE, trip.rounding, trip.onlyRoundUp)
  const calculatedRoundedY = round(calculatedFinalY, trip.rounding, trip.onlyRoundUp)

  const forcedY = trip.forcedPriceYuan
  const forcedE = trip.forcedPriceEur

  const finalE = (trip.useForcedPrice ? forcedE : calculatedRoundedE)
  const finalY = (trip.useForcedPrice ? forcedY : calculatedRoundedY)

  return {
    calculatedBruteE,
    calculatedBruteY,
    factorGestion,
    calculatedFinalE,
    calculatedFinalY,
    calculatedRoundedE,
    calculatedRoundedY,
    forcedE,
    forcedY,
    finalE,
    finalY
  }
}

// TODO MEMOIZE
export const calculateTripPriceFromBill = _calculateTripPriceFromBill

export const _calculateTripPrice = (trip, route, Activities, Cities) => {
  if (LOG_CACHE) console.log(`CACHE MISS -- _calculateTripPrice(trip ${trip ? trip._id : trip}, route ${route ? route._id : route}, ${size(Activities)} activities, ${size(Cities)} cities)`)
  const bill = getTripBill(trip, route, Activities, Cities)
  return calculateTripPriceFromBill(trip, bill)
}

export const calculateTripPrice = memoize(TRIPS_CACHE)(_calculateTripPrice)

export const getItemFee = (item, defaultFee) => (
  (item.useCustomFee ? (100 + (item.customFee || 0)) / 100 : defaultFee) || 1
)

export const getFactorGestion = trip => (100 + trip.pGestion) / 100

// #TODO this should probably consider wether the item is
export const getItemPricesAndFees = (item, factorGestion, itemsDict, Activities) => {
  const price = (
    ((item.type == "city") && (item.transport ? item.transport.price : emptyPrice))
    || ((item.type == "expense" || item.type == "hotel")
      && (item.expense || itemsDict[item.ref].expense).price)
    || ((item.type == "activity")
      && (
        (item.useCustomPrice && (item.customPrice || emptyPrice))
        || item.frozenPrice
        || Activities[item.id].price
      ))
    || (item.type == "day" && emptyPrice)
    || (l(`WTF IS THIS? ${JSON.stringify(item)}`) && emptyPrice))

  const name = (
    ((item.type == "city") && (item.transport ? item.transport.means : "Transport IDK"))
    || ((item.type == "expense" || item.type == "hotel")
      && (item.expense || itemsDict[item.ref].expense).name)
    || ((item.type == "activity")
      && (Activities[item.id].name))
    || ((item.type == "day")
      && ("Day XXX"))
    || "IDK WHAT")

  const fee = getItemFee(item, factorGestion)

  const key = item.key
  const type = item.type

  return { key, name, type, price, fee }
}

// price2
export const calculateTripPrice2 = (trip, route, Activities) => {
  alert("ERROR calculateTripPrice2 has bugs (at least on transport items with custom fees)")
  console.error("ERROR calculateTripPrice2 has bugs (at least on transport items with custom fees)")

  if (LOG_CACHE) console.log(`CACHE MISS -- _calculateTripPrice2(trip ${trip._id}, route ${route._id}, ${size(Activities)} activities)`)

  const itemsDict = chain(route.agenda).filter("key").keyBy("key").value()
  const factorGestion = getFactorGestion(trip)

  const pricesAndFees: { price: Object, fee: number }[] = [
    ...route.agenda.map(item => getItemPricesAndFees(item, factorGestion, itemsDict, Activities)),
    ...route.expenses.map(item => ({ price: item.price, fee: factorGestion }))
  ]

  const adults = countAdults(trip)
  const kids = countKids(trip)

  const brutes = pricesAndFees.map(({ price }) => calculateItemPrice(price, trip.people))
  const calculatedBruteE = sum(brutes)

  const calculatedBruteY = eur2yuan(calculatedBruteE)

  const calculatedFinalE = sum(pricesAndFees.map(({ price, fee }) => (
    fee * calculateItemPrice(price, trip.people)
  )))

  const calculatedFinalY = eur2yuan(calculatedFinalE)
  const calculatedRoundedE = round(calculatedFinalE, trip.rounding, trip.onlyRoundUp)
  const calculatedRoundedY = round(calculatedFinalY, trip.rounding, trip.onlyRoundUp)
  const forcedY = trip.forcedPriceYuan
  const forcedE = trip.forcedPriceEur
  const finalE = (trip.useForcedPrice ? forcedE : calculatedRoundedE)
  const finalY = (trip.useForcedPrice ? forcedY : calculatedRoundedY)

  return {
    calculatedBruteE,
    calculatedBruteY,
    factorGestion,
    calculatedFinalE,
    calculatedFinalY,
    calculatedRoundedE,
    calculatedRoundedY,
    forcedE,
    forcedY,
    finalE,
    finalY
  }
}

export const printBill = memoize(3)((trip, bill) => {
  const main = bill.map(section => (
    ` - ${section.sectionName}\n${section.items.map(item => ` - - ${item.name} : ${item.priceValue}\n`).join("")}`
  ))
    .join("")

  const final = calculateTripPriceFromBill(trip, bill).finalE

  return `${main}\nTotal: ${final}€`
})

/**
 *
 */

export function getTotalCommissions(trip) {
  return trip.commission + sum(trip.commissions.map(x => x.value))
}

/**
 *
 */

export type PaidTotalsType = {
  eur: number,
  yuan: number,
  receivedEur: number
}

export function getPaidTotals(trip: Object): PaidTotalsType {
  if (trip.payments) {
    const r = sum(trip.payments.map(p => (p.isReceived ? p.receivedEur : 0)))
    return {
      eur: sum(trip.payments.map(p => p.eur)),
      yuan: sum(trip.payments.map(p => p.yuan)),
      receivedEur: r,
      receivedYuan: eur2yuan(r),
    }
  }
  else {
    return {
      eur: 0,
      yuan: 0,
      receivedEur: 0,
      receivedYuan: 0,
    }
  }
}

/**
 * Reservation-handling
 */

export const getUnusedReservations = memoize(4)(
  (tripIgnored: Object, tripReservations: { [string]: Object }, route: Object, billIgnored) => {
    const usedKeys = new Set([
      ...route.agenda.filter(x => x.key).map(x => x.key),
      ...route.expenses.filter(x => x._id).map(x => x._id)
    ])

    return values(tripReservations).filter(r => !usedKeys.has(r.itemKey)).map(r => r.itemKey)
  }
)

// #TODO also control repetitionDates (and, then, dont do date)
export const getBrokenReservations = memoize(4)((tripIgnored, tripReservations, routeIgnored, bill) => {
  const billItems = compact(flatten(compact(bill.map(section => section.items))))
  const billItemsD = keyBy(billItems, "key")

  function getReservationChanges(r) {
    const b = billItemsD[r.itemKey]
    const changes = []

    if (b) {
      if (b.repetitionDates && !isEqual(b.repetitionDates, r.repetitionDates)) {
        changes.push({ field: "repetitionDates", newValue: b.repetitionDates, rValue: r.repetitionDates })
      }
      else if ((b.date || r.date) && b.date != r.date) {
        changes.push({ field: "date", newValue: b.date, rValue: r.date })
      }

      ["name", "time", "origin", "destination", "numRepetitions"]
        .forEach(k => {
          if ((b[k] || r[k]) && b[k] != r[k]) {
            changes.push({ field: k, newValue: b[k], rValue: r[k] })
          }
        })
    }

    return (changes.length ? changes : null)
  }

  const broken = values(tripReservations)
    .map(r => {
      const changes = getReservationChanges(r)
      return changes ? { k: r.itemKey, changes } : null
    })
    .filter(x => x)

  const brokenReservations = {}
  broken.forEach(x => { brokenReservations[x.k] = x.changes })

  return brokenReservations
})

/**
 *
 */

export const getTripCode = t => t._id.substr(t._id.length - 5).toUpperCase()

/**
 * Promises to await seconds
 */

export const msPromise = n => new Promise(yea => {
  setTimeout(() => yea(n), n)
})

export const secondsPromise = n => msPromise(n * 1000)
